import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { POI_TYPES } from '../../constants/poiTypes';
import { COLLECTIONS } from '@utils/database/collections';
import { logError, logInfo } from '@utils/logger';
import { collection, addDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../../../../config/firebase';
import { Toast } from '../../../../components/common/Toast';
import { useLocationTracking } from '@hooks/useLocationTracking';
import { useNavigate } from 'react-router-dom';
import { useSurvey } from '../../../../contexts/SurveyContext';
import { savePendingData } from '../../../../utils/offlineStorage';
import { generatePOIId } from '@utils/idGenerators';
import { POIForm } from './POIForm';

export const POISelector = ({ location, onClose, onSave }) => {
  const { userLocation } = useLocationTracking();
  const navigate = useNavigate();
  const { activeSurvey } = useSurvey();
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [poiInfo, setPoiInfo] = useState({
    id: null,
    coordinates: location || userLocation ? {
      latitude: location?.lat || userLocation?.lat,
      longitude: location?.lng || userLocation?.lng
    } : null,
    address: 'Getting address...'
  });

  // Generate POI ID when type is selected
  useEffect(() => {
    if (selectedType) {
      const id = generatePOIId('QK', selectedType, 1, Date.now());
      setPoiInfo(prev => ({
        ...prev,
        id: id
      }));
    }
  }, [selectedType]);

  // Update POI info when location changes
  useEffect(() => {
    if (location) {
      setPoiInfo(prev => ({
        ...prev,
        coordinates: {
          latitude: location.lat,
          longitude: location.lng
        }
      }));
      logInfo('POI location updated from props:', location);
    } else if (userLocation) {
      setPoiInfo(prev => ({
        ...prev,
        coordinates: {
          latitude: userLocation.lat,
          longitude: userLocation.lng
        }
      }));
      logInfo('POI location updated from user location:', userLocation);
    }
  }, [location, userLocation]);

  // Get nearest address when coordinates change
  useEffect(() => {
    const getAddress = async () => {
      if (poiInfo.coordinates) {
        try {
          const address = await getNearestAddress(
            poiInfo.coordinates.latitude,
            poiInfo.coordinates.longitude
          );
          setPoiInfo(prev => ({
            ...prev,
            address: address || 'Address not found'
          }));
        } catch (error) {
          console.error('Failed to get address:', error);
          setPoiInfo(prev => ({
            ...prev,
            address: 'Failed to get address'
          }));
        }
      }
    };
    getAddress();
  }, [poiInfo.coordinates]);

  const handleSave = async (data, selectedType) => {
    try {
      setSaving(true);
      setError(null);
      setToast(null);

      if (!activeSurvey) {
        throw new Error('No active survey selected');
      }

      // Ensure we have valid coordinates
      if (!poiInfo.coordinates?.latitude || !poiInfo.coordinates?.longitude) {
        throw new Error('Location coordinates are required');
      }

      // Add coordinates to POI data
      const poiData = {
        ...data,
        coordinates: poiInfo.coordinates,
        type: selectedType,
        surveyId: activeSurvey.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Save POI to Firestore or pending queue
      if (!navigator.onLine) {
        await savePendingData(COLLECTIONS.POIS, poiData);
        logInfo('POI saved for offline sync');
        setToast({
          type: 'success',
          message: 'POI saved for offline sync',
          zIndex: 1000
        });
        setTimeout(() => onClose(), 1500);
        return { success: true };
      } else {
        const docRef = await addDoc(collection(db, COLLECTIONS.POIS), poiData);
        logInfo('POI saved successfully with ID:', docRef.id);
        
        if (onSave) {
          await onSave(poiData);
        }
        
        setToast({
          type: 'success',
          message: 'POI saved successfully',
          zIndex: 1000
        });
        
        setTimeout(() => onClose(), 1500);
        return { success: true };
      }
    } catch (error) {
      logError('Failed to save POI:', error);
      setError(error.message || 'Failed to save POI');
      setToast({
        type: 'error',
        message: error.message || 'Failed to save POI',
        zIndex: 1000
      });
      return { success: false, error: error.message };
    } finally {
      setSaving(false);
    }
  };

  // Render type-specific form if type is selected
  if (selectedType) {
    return (
      <POIForm
        poiInfo={poiInfo}
        type={selectedType}
        onClose={onClose}
        onSave={(data) => handleSave(data, selectedType)}
        onBack={() => setSelectedType(null)}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-[300] overflow-y-auto">
      <div className="bg-[#2A2B2E] rounded-lg shadow-lg w-full max-w-md">
        {/* Toast Notifications */}
        <div className="fixed top-4 right-4 z-[1000]">
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
        </div>

        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-white">Select POI Type</h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-lg"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        
        {error && (
          <div className="p-4 mb-4 bg-red-500/10 text-red-400 rounded-lg">
            {error}
          </div>
        )}

        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(POI_TYPES).map(([type, config]) => {
              const Icon = config.icon;
              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  disabled={saving}
                  className="flex flex-col items-center justify-center p-4 bg-gray-700/50 rounded-xl hover:bg-gray-600/50 transition-colors"
                >
                  <div 
                    className="w-12 h-12 rounded-full mb-2 flex items-center justify-center"
                    style={{ backgroundColor: `${config.color}20`, color: config.color }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className="text-sm font-medium text-white">{type}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get nearest address
const getNearestAddress = async (latitude, longitude) => {
  try {
    if (!latitude || !longitude || isNaN(latitude) || isNaN(longitude)) {
      throw new Error('Invalid coordinates');
    }

    console.log('Getting address for:', latitude, longitude);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
    );
    if (!response.ok) {
      console.error('Address lookup failed:', response.status);
      throw new Error('Failed to fetch address');
    }
    const data = await response.json();
    console.log('Address lookup response:', data);
    return data.display_name || 'Address not found';
  } catch (error) {
    console.error('Failed to get address:', error);
    return 'Failed to get address';
  }
};